/**
 * Vuex module for menu access control
 * Fetches and manages user's allowed menu items based on role permissions
 */
import apiClient from '../../services/api'

// The in-flight request, held OUTSIDE Vuex state on purpose: the store runs in
// strict mode in dev (state/store.js), and a Promise is neither serialisable
// nor something we want to mutate through a mutation. Both the sidebar
// (components/menu.vue, on created) and the router guard (router/index.js) ask
// for menus during initial load; without this, a fast click on a gated route
// while the sidebar is still booting issues two identical requests.
let pendingFetch = null

export const state = {
    userMenus: [],                // Hierarchical menu tree (the API returns [] — see below)
    flatMenuIds: [],             // Flat array of allowed menu IDs — the real payload
    loading: false,
    error: null,
    loadFailed: false,           // sticky until the next successful fetch
    lastFetched: null,
    cacheTimeout: 5 * 60 * 1000  // 5 minutes cache
}

export const mutations = {
    SET_USER_MENUS(state, { data, flatMenuIds }) {
        state.userMenus = data || []
        state.flatMenuIds = flatMenuIds || []
        state.lastFetched = new Date().getTime()
        state.error = null
        state.loadFailed = false
    },

    SET_LOADING(state, loading) {
        state.loading = loading
    },

    SET_ERROR(state, error) {
        state.error = error
        state.loading = false
        state.loadFailed = true
    },

    CLEAR_MENUS(state) {
        state.userMenus = []
        state.flatMenuIds = []
        state.lastFetched = null
        state.error = null
        state.loadFailed = false
    }
}

export const getters = {
    /**
     * Check if user can access a specific menu item
     * @param {string} menuId - Menu item ID (e.g., 'hwc.counties')
     * @returns {boolean}
     */
    canAccessMenu: (state) => (menuId) => {
        return state.flatMenuIds.includes(menuId)
    },

    /**
     * Get menu tree for rendering
     */
    menuTree(state) {
        return state.userMenus
    },

    /**
     * Check if menus are loaded.
     *
     * Keyed on `lastFetched`, NOT on `userMenus.length`. /admin-menu/user-menus
     * returns `data: []` unconditionally (core_admin.py user_menus) — the whole
     * payload is `flat_menu_ids`. Testing the tree length therefore reported
     * "not loaded" forever, so the cache never hit and the router guard
     * re-fetched on every gated navigation.
     */
    menusLoaded(state) {
        return state.lastFetched !== null
    },

    /**
     * True when the last fetch attempt errored. The sidebar renders its FULL
     * menu in this state rather than an empty one — it is not a security
     * boundary (the router guard and every endpoint still enforce), and the
     * guard itself fails open on the same error (router/index.js).
     */
    menuLoadFailed(state) {
        return state.loadFailed
    },

    /**
     * Check if cache is valid
     */
    isCacheValid(state) {
        if (!state.lastFetched) return false
        const now = new Date().getTime()
        return (now - state.lastFetched) < state.cacheTimeout
    }
}

export const actions = {
    /**
     * Fetch user's allowed menus from API
     * Uses cache if available and valid
     */
    async fetchUserMenus({ commit, getters }, { force = false } = {}) {
        // Return cached data if valid and not forcing refresh
        if (!force && getters.isCacheValid && getters.menusLoaded) {
            console.log('[MenuAccess] Using cached menus')
            return Promise.resolve(getters.menuTree)
        }

        // Coalesce concurrent callers onto one request (see `pendingFetch` above).
        if (pendingFetch && !force) return pendingFetch

        commit('SET_LOADING', true)

        pendingFetch = (async () => {
            try {
                console.log('[MenuAccess] Fetching user menus from API')
                const response = await apiClient.get('/admin-menu/user-menus')

                if (response.data.success) {
                    commit('SET_USER_MENUS', {
                        data: response.data.data,
                        flatMenuIds: response.data.flat_menu_ids || []
                    })
                    console.log('[MenuAccess] Menus loaded:',
                        (response.data.flat_menu_ids || []).length, 'accessible ids')
                    return response.data.data
                } else {
                    throw new Error(response.data.message || 'Failed to fetch menus')
                }
            } catch (error) {
                console.error('[MenuAccess] Error fetching menus:', error)

                // If unauthorized, clear menus
                if (error.response && error.response.status === 401) {
                    commit('CLEAR_MENUS')
                } else {
                    commit('SET_ERROR', error.message || 'Failed to load menus')
                }

                throw error
            } finally {
                commit('SET_LOADING', false)
            }
        })()

        try {
            return await pendingFetch
        } finally {
            pendingFetch = null
        }
    },

    /**
     * Clear menu cache (e.g., on logout)
     */
    clearMenus({ commit }) {
        console.log('[MenuAccess] Clearing menu cache')
        // Drop any in-flight request too: it was issued for the OUTGOING user
        // and its result must not be adopted by whoever logs in next.
        pendingFetch = null
        commit('CLEAR_MENUS')
    },

    /**
     * Refresh menus (force reload from API)
     */
    async refreshMenus({ dispatch }) {
        console.log('[MenuAccess] Forcing menu refresh')
        return dispatch('fetchUserMenus', { force: true })
    },

    /**
     * Initialize module - called on app startup
     */
    async init({ dispatch, rootGetters }) {
        // Only fetch if user is logged in
        if (rootGetters['auth/loggedIn']) {
            try {
                await dispatch('fetchUserMenus')
            } catch (error) {
                console.error('[MenuAccess] Failed to initialize menus:', error)
            }
        }
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions
}
